import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../services/api';
import UserMenu from './UserMenu';

const ProjectHistory = ({ userName, onLogout }) => {
  const navigate = useNavigate();
  const [projetos, setProjetos] = useState([]);
  const [allProjetos, setAllProjetos] = useState([]); // Para obter listas únicas de setores e gestores
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dadosCarregados, setDadosCarregados] = useState(false); // Flag para saber se já carregou dados iniciais
  const [isLoaded, setIsLoaded] = useState(false); // Para animação de entrada
  const [isFiltering, setIsFiltering] = useState(false); // Para animação durante filtro
  const [tableKey, setTableKey] = useState(0); // Key para forçar re-render com animação
  
  // Filtros
  const [pesquisa, setPesquisa] = useState('');
  const [setorFiltro, setSetorFiltro] = useState('');
  const [gestorFiltro, setGestorFiltro] = useState('');

  useEffect(() => {
    // Animação de entrada suave
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // Sem token, volta para o início/login
      navigate('/');
      return;
    }
    loadProjetos();
  }, []);

  useEffect(() => {
    // Só aplicar filtros se os dados já foram carregados inicialmente
    if (!dadosCarregados) return;
    
    // Aplicar filtros quando eles mudarem (com debounce para pesquisa)
    const timeoutId = setTimeout(async () => {
      try {
        setIsLoading(true);
        setIsFiltering(true);
        
        const filtros = {};
        
        // Só adicionar filtros que não estão vazios
        if (pesquisa && pesquisa.trim()) {
          filtros.pesquisa = pesquisa.trim();
        }
        if (setorFiltro && setorFiltro.trim()) {
          filtros.setor = setorFiltro.trim();
        }
        if (gestorFiltro && gestorFiltro.trim()) {
          filtros.gestor = gestorFiltro.trim();
        }
        
        const response = await apiService.getProjetosSolicitacoes(filtros);
        if (response.success && response.data) {
          // Pequeno delay para animação mais suave
          setTimeout(() => {
            setProjetos(response.data);
            setTableKey(prev => prev + 1); // Forçar re-render com animação
            setIsFiltering(false);
            setIsLoading(false);
          }, 150);
        } else {
          setIsFiltering(false);
          setIsLoading(false);
        }
      } catch (error) {
        toast.error('Erro ao filtrar projetos');
        setIsFiltering(false);
        setIsLoading(false);
      }
    }, pesquisa ? 300 : 0); // Debounce de 300ms para pesquisa

    return () => clearTimeout(timeoutId);
  }, [pesquisa, setorFiltro, gestorFiltro, dadosCarregados]);

  const loadProjetos = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiService.getProjetosSolicitacoes();
      if (response.success && response.data) {
        setAllProjetos(response.data);
        setProjetos(response.data);
        setDadosCarregados(true); // Marcar que dados foram carregados
      } else {
        throw new Error('Erro ao carregar projetos');
      }
    } catch (error) {
      if (error.message.includes('403') || error.message.includes('Acesso negado')) {
        setError('Você não possui permissão para acessar as solicitações de projetos.');
        toast.error('Acesso negado. Você não possui permissão para acessar esta página.');
      } else {
        setError('Erro ao carregar solicitações de projetos. Tente novamente.');
        toast.error('Erro ao carregar solicitações de projetos');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const limparFiltros = async () => {
    setPesquisa('');
    setSetorFiltro('');
    setGestorFiltro('');
    // Recarregar todos os projetos sem filtros
    try {
      setIsLoading(true);
      const response = await apiService.getProjetosSolicitacoes({});
      if (response.success && response.data) {
        setProjetos(response.data);
        setAllProjetos(response.data);
      }
    } catch (error) {
      toast.error('Erro ao recarregar projetos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjetoClick = (identificadorUnico) => {
    // Abrir matriz em nova aba
    const url = `${window.location.origin}/projetos/${identificadorUnico}`;
    window.open(url, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const hasTZ = /Z|[+-]\d{2}:?\d{2}$/.test(dateString);
      const normalized = hasTZ ? dateString : `${dateString.replace(' ', 'T')}Z`;
      const date = new Date(normalized);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  };

  // Obter setores únicos para o dropdown
  const setoresUnicos = [...new Set(allProjetos.map(p => p.solicitante_setor).filter(Boolean))].sort();
  
  // Obter gestores únicos para o dropdown
  const gestoresUnicos = [...new Set(allProjetos.map(p => p.gestor_nome).filter(Boolean))].sort();
  
  // Verificar se há filtros ativos
  const temFiltros = pesquisa.trim() || setorFiltro || gestorFiltro;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* CSS para animações */}
      <style>{`
        @keyframes fadeInRow {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeInTable {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animation-delay-150 {
          animation-delay: 150ms;
        }
        .animation-delay-300 {
          animation-delay: 300ms;
        }
      `}</style>
      {/* Header Moderno CEDISA */}
      <div className="relative z-10 bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          {/* Título da página à esquerda */}
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: '#090E3A' }}>
              Solicitações de Projetos Propostos
            </h1>
            <p className="text-sm text-gray-600 font-medium">Visualização completa • CEDISA</p>
          </div>
          
          {/* Logo CEDISA centralizada (botão para home) */}
          <div className="flex-1 flex justify-center">
            <button 
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity duration-200"
            >
              <img src="/cedisa-logo.png" alt="Cedisa Logo" className="h-10 drop-shadow-sm" />
            </button>
          </div>
          
          {/* Nome do usuário na direita */}
          <div className="flex-1 flex justify-end">
            <UserMenu userName={userName} onLogout={onLogout} />
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className={`max-w-7xl mx-auto px-6 py-8 transition-all duration-700 ${
        isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando solicitações de projetos...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700 font-semibold">{error}</p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Voltar ao Início
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            {/* Header da Tabela com Filtros - Sempre visível e fixo (não anima) */}
            <div className="bg-gray-100 px-6 py-4 border-b border-gray-200" style={{ position: 'relative', zIndex: 10 }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold transition-all duration-300" style={{ color: '#090E3A' }}>
                  {projetos.length > 0 ? (
                    `Projetos Solicitados (${projetos.length})`
                  ) : allProjetos.length > 0 ? (
                    'Nenhum projeto encontrado'
                  ) : (
                    'Projetos Solicitados (0)'
                  )}
                </h2>
                {temFiltros && (
                  <button
                    onClick={limparFiltros}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Limpar Filtros
                  </button>
                )}
              </div>
              
              {/* Barra de Filtros - Sempre visível e fixa (não anima) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Campo de Pesquisa */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Pesquisar por nome do projeto..."
                    value={pesquisa}
                    onChange={(e) => setPesquisa(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 hover:border-gray-400"
                  />
                </div>
                
                {/* Filtro por Setor */}
                <div>
                  <select
                    value={setorFiltro}
                    onChange={(e) => setSetorFiltro(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 hover:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todos os setores</option>
                    {setoresUnicos.map((setor) => (
                      <option key={setor} value={setor}>
                        {setor}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Filtro por Gestor */}
                <div>
                  <select
                    value={gestorFiltro}
                    onChange={(e) => setGestorFiltro(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 hover:border-gray-400 cursor-pointer"
                  >
                    <option value="">Todos os gestores</option>
                    {gestoresUnicos.map((gestor) => (
                      <option key={gestor} value={gestor}>
                        {gestor}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Conteúdo abaixo: Tabela ou Mensagem - Esta parte anima */}
            <div className="relative" style={{ minHeight: '200px' }}>
              {isFiltering ? (
                // Animação de transição durante filtro (apenas esta área)
                <div className="p-8 text-center">
                  <div className="animate-pulse flex items-center justify-center space-x-2">
                    <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                    <div className="h-2 w-2 bg-blue-600 rounded-full animation-delay-150"></div>
                    <div className="h-2 w-2 bg-blue-600 rounded-full animation-delay-300"></div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Aplicando filtros...</p>
                </div>
              ) : projetos.length === 0 ? (
                // Mensagem quando não há projetos após filtros (mas há projetos no sistema)
                <div 
                  key={`empty-${tableKey}`}
                  className="p-8 text-center"
                  style={{
                    animation: 'fadeInTable 0.4s ease-out'
                  }}
                >
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Nenhum projeto encontrado</h3>
                  {temFiltros ? (
                    <p className="text-gray-600 mb-4">
                      Nenhum projeto corresponde aos filtros aplicados. Tente ajustar sua busca ou limpar os filtros.
                    </p>
                  ) : allProjetos.length === 0 ? (
                    <p className="text-gray-600">Ainda não há projetos cadastrados no sistema.</p>
                  ) : (
                    <p className="text-gray-600">Nenhum projeto encontrado.</p>
                  )}
                </div>
              ) : (
                // Tabela com projetos - com animação fluida
                <div 
                  key={tableKey}
                  className="overflow-x-auto"
                  style={{
                    animation: 'fadeInTable 0.4s ease-out'
                  }}
                >
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nome do Projeto
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Setor
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gestor
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data de Criação
                      </th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {projetos.map((projeto, index) => (
                      <tr
                        key={projeto.id}
                        className="hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                        style={{
                          animation: `fadeInRow 0.3s ease-out ${index * 0.03}s both`
                        }}
                        onClick={() => handleProjetoClick(projeto.identificador_unico)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">
                            {projeto.nome_projeto || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {projeto.solicitante_setor || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            {projeto.gestor_nome || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(projeto.data_criacao)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProjetoClick(projeto.identificador_unico);
                            }}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white transition-colors"
                            style={{ backgroundColor: '#E85C1E' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#d14a16'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#E85C1E'}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            Abrir Matriz
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectHistory;

